// AUTO-GENERATED by apps/site/scripts/build-field-notes.mjs — do not edit by hand.
// Source: apps/site/content/notes/<slug>/{meta.json,index.html}, the site's own
// copy of the Frontier Field Notes (seeded from the lalsaado wiki, edited here).
// Refresh: `pnpm --filter @ffn/site run content:field-notes`.

import type { FieldNoteMeta } from "./field-notes.js"

export const FIELD_NOTES_INDEX: ReadonlyArray<FieldNoteMeta> = [
  {
    slug: "agent-evals",
    kind: "essay",
    number: 5,
    title: "Agent Evals That Show When Your AI Agent Is Getting Worse",
    abstract:
      "Catch regressions before deployment and bad output during a run. A comparison of seven eval frameworks and the feedback loops that make them useful.",
    tags: ["Evals", "Regression", "Quality"],
    byline: "by Laith Al-Saadoon",
    modified: "2026-09-11",
    relatedSlugs: ["yield-engineering", "autonomous-work-horizon"],
    date: "March 19, 2026",
    dateSort: "2026-03-19",
    headings: [
      {
        id: "measure-the-path-not-just-the-answer",
        text: "Measure the path, not just the answer",
        level: 2
      },
      {
        id: "two-complementary-evaluation-loops",
        text: "Two complementary evaluation loops",
        level: 2
      },
      {
        id: "choose-a-framework-for-the-failure-you-need-to-see",
        text: "Choose a framework for the failure you need to see",
        level: 2
      },
      {
        id: "turn-failures-into-regression-tests",
        text: "Turn failures into regression tests",
        level: 2
      },
      {
        id: "references",
        text: "References",
        level: 2
      }
    ],
    readingMinutes: 6
  },
  {
    slug: "agent-needs-computer",
    kind: "essay",
    number: 4,
    title: "Why Coding Agents Need a Computer That Isn't Your Laptop",
    abstract:
      "The coding agent harness is absorbing knowledge work, and laptops fail it at team scale. Agents need isolated, durable, scalable cloud compute to run for hours.",
    tags: ["Agents", "Infrastructure", "Cloud"],
    byline: "by Laith Al-Saadoon",
    modified: "2026-09-11",
    relatedSlugs: ["agent-substrate", "llm-code-obsession"],
    date: "March 12, 2026",
    dateSort: "2026-03-12",
    headings: [
      {
        id: "the-coding-harness-is-becoming-a-work-environment",
        text: "The coding harness is becoming a work environment",
        level: 2
      },
      {
        id: "from-model-capability-to-runtime-design",
        text: "From model capability to runtime design",
        level: 2
      },
      {
        id: "isolation-durability-and-portability",
        text: "Isolation, durability, and portability",
        level: 2
      },
      {
        id: "choose-the-runtime-your-team-can-operate",
        text: "Choose the runtime your team can operate",
        level: 2
      },
      {
        id: "references",
        text: "References",
        level: 2
      }
    ],
    readingMinutes: 7
  },
  {
    slug: "agent-substrate",
    kind: "essay",
    number: 7,
    title: "Self-Improving AI Agents Will Reduce the Code You Ship",
    abstract:
      "Given one sentence, an agent pulled data, ran statistics, wrote a brief and kept what it learned in six minutes. Most software we ship is plumbing it can write.",
    tags: ["Self-Improving Agents", "Tools", "Architecture"],
    byline: "by Laith Al-Saadoon",
    modified: "2026-09-11",
    relatedSlugs: ["erpaval", "agent-needs-computer"],
    date: "June 4, 2026",
    dateSort: "2026-06-04",
    headings: [
      {
        id: "the-run-in-numbers",
        text: "The run, in numbers",
        level: 2
      },
      {
        id: "you-have-built-every-piece-of-this",
        text: "You have built every piece of this",
        level: 2
      },
      {
        id: "the-run-needed-no-deploy",
        text: "The run needed no deploy",
        level: 2
      },
      {
        id: "most-software-is-json-with-a-commute",
        text: "Most software is JSON with a commute",
        level: 2
      },
      {
        id: "you-still-own-correctness",
        text: "You still own correctness",
        level: 2
      }
    ],
    readingMinutes: 6
  },
  {
    slug: "autonomous-work-horizon",
    kind: "essay",
    number: 3,
    title: "The Autonomous Work Horizon Is Doubling Every Five Months",
    abstract:
      "A March 2026 analysis of METR task horizons: a 4.8-month doubling trend, its reliability limits, and why human task duration is not agent runtime.",
    tags: ["Autonomy", "Benchmarks", "METR"],
    byline: "by Laith Al-Saadoon",
    modified: "2026-09-11",
    relatedSlugs: ["agent-evals", "agent-needs-computer"],
    date: "March 5, 2026",
    dateSort: "2026-03-05",
    headings: [
      {
        id: "what-the-march-2026-analysis-measured",
        text: "What the March 2026 analysis measured",
        level: 2
      },
      {
        id: "human-task-duration-is-not-agent-runtime",
        text: "Human task duration is not agent runtime",
        level: 2
      },
      {
        id: "reliability-and-limits",
        text: "Reliability and limits",
        level: 2
      },
      {
        id: "references",
        text: "References",
        level: 2
      }
    ],
    readingMinutes: 6
  },
  {
    slug: "closing-the-loop",
    kind: "essay",
    number: 2,
    title: "Closing the Loop on AI-Generated Code with Automated Review",
    abstract:
      "Independent AI review can catch design problems that linters miss. Build a review loop that complements tests, static analysis, and human judgment.",
    tags: ["Code Review", "Quality", "AI-Generated Code"],
    byline: "by Laith Al-Saadoon",
    modified: "2026-09-11",
    relatedSlugs: ["symspec", "erpaval"],
    date: "February 12, 2026",
    dateSort: "2026-02-12",
    headings: [
      {
        id: "linters-are-table-stakes",
        text: "Linters are table stakes",
        level: 2
      },
      {
        id: "the-judgment-gap",
        text: "The judgment gap",
        level: 2
      },
      {
        id: "context-isolation-is-the-fix",
        text: "Context isolation is the fix",
        level: 2
      },
      {
        id: "what-the-reviewer-evaluates",
        text: "What the reviewer evaluates",
        level: 2
      },
      {
        id: "the-severity-contract",
        text: "The severity contract",
        level: 2
      },
      {
        id: "a-real-example",
        text: "A real example",
        level: 2
      },
      {
        id: "extending-to-your-stack",
        text: "Extending to your stack",
        level: 2
      },
      {
        id: "the-playbook",
        text: "The playbook",
        level: 2
      },
      {
        id: "adding-this-to-your-workflow",
        text: "Adding this to your workflow",
        level: 2
      }
    ],
    readingMinutes: 6
  },
  {
    slug: "erpaval",
    kind: "essay",
    number: 6,
    title: "ERPAVal, a Gated Six-Phase Workflow for Claude Code Agents",
    abstract:
      "ERPAVal runs Claude Code agents through six gated phases and writes what each session learned to disk, so the next run starts with yesterday's lessons.",
    tags: ["Autonomous Dev", "Workflow", "Claude Code"],
    byline: "by Laith Al-Saadoon",
    modified: "2026-09-11",
    relatedSlugs: ["symspec", "closing-the-loop"],
    projectUrl: "https://github.com/laithalsaadoon/erpaval",
    date: "May 14, 2026",
    dateSort: "2026-05-14",
    headings: [
      {
        id: "the-problem",
        text: "The problem",
        level: 2
      },
      {
        id: "the-six-phases",
        text: "The six phases",
        level: 2
      },
      {
        id: "before-the-phases-adaptive-intake",
        text: "Before the phases: adaptive intake",
        level: 2
      },
      {
        id: "the-phases-in-detail",
        text: "The phases in detail",
        level: 2
      },
      {
        id: "0-session-0-the-mandatory-intake-scaffold",
        text: "0. Session 0, the mandatory intake scaffold",
        level: 3
      },
      {
        id: "1-explore",
        text: "1. Explore",
        level: 3
      },
      {
        id: "2-research",
        text: "2. Research",
        level: 3
      },
      {
        id: "3-plan",
        text: "3. Plan",
        level: 3
      },
      {
        id: "4-act",
        text: "4. Act",
        level: 3
      },
      {
        id: "5-validate",
        text: "5. Validate",
        level: 3
      },
      {
        id: "6-compound",
        text: "6. Compound",
        level: 3
      },
      {
        id: "why-gates-matter",
        text: "Why gates matter",
        level: 2
      },
      {
        id: "the-closed-loop-toolchain",
        text: "The closed-loop toolchain",
        level: 2
      },
      {
        id: "when-to-use-erpaval",
        text: "When to use ERPAVal",
        level: 2
      },
      {
        id: "in-practice",
        text: "In practice",
        level: 2
      }
    ],
    readingMinutes: 15
  },
  {
    slug: "fab-tools-signal",
    kind: "sidequest",
    number: null,
    title: "Chip Equipment Stocks as a Lead Indicator of the AI Buildout",
    abstract:
      "In the June 2026 AI hardware selloff the chip designers fell and the equipment makers did not. Equipment orders run four to six quarters ahead of demand.",
    tags: ["Markets", "Semiconductors", "AI Buildout"],
    byline: "by Laith Al-Saadoon",
    modified: "2026-09-11",
    relatedSlugs: ["autonomous-work-horizon", "yield-engineering"],
    date: "June 11, 2026",
    dateSort: "2026-06-11",
    headings: [],
    readingMinutes: 2
  },
  {
    slug: "llm-code-obsession",
    kind: "essay",
    number: 1,
    title: "Why Frontier AI Labs Are Obsessed with Coding Abilities",
    abstract:
      "Code is the most general interface to the modern economy. It reaches APIs, terminals, files, and databases, so labs build agents that turn intent into action.",
    tags: ["Frontier Models", "Coding Agents", "Economics"],
    byline: "by Laith Al-Saadoon",
    modified: "2026-09-11",
    relatedSlugs: ["autonomous-work-horizon", "agent-needs-computer"],
    date: "January 8, 2026",
    dateSort: "2026-01-08",
    headings: [
      {
        id: "code-as-a-way-to-do-the-work",
        text: "Code as a way to do the work",
        level: 2
      },
      {
        id: "beyond-apis",
        text: "Beyond APIs",
        level: 2
      },
      {
        id: "task-length-and-reliable-delegation",
        text: "Task length and reliable delegation",
        level: 2
      },
      {
        id: "capability-is-not-adoption",
        text: "Capability is not adoption",
        level: 2
      },
      {
        id: "references",
        text: "References",
        level: 2
      }
    ],
    readingMinutes: 5
  },
  {
    slug: "remarkable-spec",
    kind: "sidequest",
    number: null,
    title: "A reMarkable Paper Pro CLI That Feeds Handwriting to Agents",
    abstract:
      "remarkable-spec parses reMarkable .rm files, renders pen strokes, and OCRs handwriting with Vision, Textract and Opus so an agent can read annotations as text.",
    tags: ["reMarkable", "OCR", "Tooling"],
    byline: "by Laith Al-Saadoon",
    modified: "2026-09-11",
    relatedSlugs: ["agent-substrate", "skill-budget-trap"],
    projectUrl: "https://github.com/laithalsaadoon/remarkable-spec",
    date: "March 12, 2026",
    dateSort: "2026-03-12",
    headings: [],
    readingMinutes: 1
  },
  {
    slug: "skill-budget-trap",
    kind: "sidequest",
    number: null,
    title: "The Skill Budget Trap That Silently Drops Claude Code Skills",
    abstract:
      "A hidden character budget silently drops Claude Code skills from context. I traced the failures, raised the budget, and added two hooks that enforce skill use.",
    tags: ["Claude Code", "Skills", "Debugging"],
    byline: "by Laith Al-Saadoon",
    modified: "2026-09-11",
    relatedSlugs: ["erpaval", "remarkable-spec"],
    date: "March 5, 2026",
    dateSort: "2026-03-05",
    headings: [
      {
        id: "the-investigation",
        text: "The investigation",
        level: 2
      },
      {
        id: "root-cause",
        text: "Root cause",
        level: 2
      },
      {
        id: "defense-in-depth",
        text: "Defense in depth",
        level: 2
      }
    ],
    readingMinutes: 2
  },
  {
    slug: "symspec",
    kind: "essay",
    number: 9,
    title: "Prove the Spec Before You Write the Code with symspec",
    abstract:
      "Find contradictions in structured requirements before writing code. symspec uses Z3 to prove conflicts and reports the limits of what it checked.",
    tags: ["Spec Verification", "Coding Agents", "SMT", "EARS"],
    byline: "by Laith Al-Saadoon",
    modified: "2026-09-11",
    relatedSlugs: ["closing-the-loop", "erpaval"],
    projectUrl: "https://github.com/laithalsaadoon/symspec",
    date: "July 9, 2026",
    dateSort: "2026-07-09",
    headings: [
      {
        id: "a-solver-can-prove-a-conflict-review-supplies-context",
        text: "A solver can prove a conflict; review supplies context.",
        level: 2
      },
      {
        id: "built-for-the-agent-to-drive",
        text: "Built for the agent to drive",
        level: 2
      },
      {
        id: "it-refuses-to-claim-more-than-it-proved",
        text: "It refuses to claim more than it proved",
        level: 2
      },
      {
        id: "the-tool-got-better-by-being-driven-by-an-agent",
        text: "The tool got better by being driven by an agent",
        level: 2
      },
      {
        id: "where-to-start",
        text: "Where to start",
        level: 2
      },
      {
        id: "references",
        text: "References",
        level: 2
      }
    ],
    readingMinutes: 9
  },
  {
    slug: "yield-engineering",
    kind: "essay",
    number: 8,
    title: "Yield Engineering for Fleets of Autonomous Coding Agents",
    abstract:
      "Shipping more pull requests is not the same as delivering useful work. Apply yield, rework, and defect measurements to a fleet of coding agents.",
    tags: ["Yield", "Metrics", "Autonomous Delivery"],
    byline: "by Laith Al-Saadoon",
    modified: "2026-09-11",
    relatedSlugs: ["agent-evals", "autonomous-work-horizon"],
    date: "June 11, 2026",
    dateSort: "2026-06-11",
    headings: [
      {
        id: "the-ground-floor-is-already-poured",
        text: "The ground floor is already poured",
        level: 2
      },
      {
        id: "the-factory-we-already-named",
        text: "The factory we already named",
        level: 2
      },
      {
        id: "the-objection-put-fairly",
        text: "The objection, put fairly",
        level: 2
      },
      {
        id: "yield-was-never-per-unit",
        text: "Yield was never per-unit",
        level: 2
      },
      {
        id: "where-this-does-not-apply",
        text: "Where this does not apply",
        level: 2
      },
      {
        id: "the-function-underneath",
        text: "The function underneath",
        level: 2
      }
    ],
    readingMinutes: 7
  }
]
